// Workforce settings (admin): shift templates + per-worker profiles. Feeds the Work
// calendar's expected-hours auto-fill. Part of Statistics Phase 3.
import React, { useEffect, useState } from 'react';
import { authFetch, API } from './api';
import HelpPanel from './HelpPanel';
import EmploymentPanel from './EmploymentPanel';
import DayThresholdPanel from './DayThresholdPanel';

interface Shift {
  id: number; name: string; start_time: string; end_time: string;
  unpaid_break_min: number; active?: boolean; sort_order?: number;
}
interface Profile {
  username: string; display_name: string; role: string; configured: boolean;
  worker_type: string; country: string; default_shift_id: number | null;
  weekly_days: number[]; active: boolean; tracked: boolean;
}

const WD = [[1, 'Mo'], [2, 'Tu'], [3, 'We'], [4, 'Th'], [5, 'Fr'], [6, 'Sa'], [7, 'Su']] as const;

export default function WorkforceSettings() {
  const [shifts, setShifts]     = useState<Shift[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [savingP, setSavingP]   = useState<string | null>(null);
  const [savingS, setSavingS]   = useState<number | null>(null);

  const load = async () => {
    const [s, p] = await Promise.all([
      authFetch(`${API}/statistics/shift-templates`).then(r => r.json()),
      authFetch(`${API}/statistics/worker-profiles`).then(r => r.json()),
    ]);
    setShifts(Array.isArray(s) ? s : []);
    setProfiles(Array.isArray(p) ? p : []);
  };
  useEffect(() => { load(); }, []);

  const patchShift = (id: number, patch: Partial<Shift>) =>
    setShifts(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
  const saveShift = async (s: Shift) => {
    setSavingS(s.id);
    await authFetch(`${API}/statistics/shift-templates/${s.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: s.name, start_time: s.start_time, end_time: s.end_time, unpaid_break_min: s.unpaid_break_min }),
    });
    setSavingS(null);
  };
  const addShift = async () => {
    await authFetch(`${API}/statistics/shift-templates`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'New shift', start_time: '07:30', end_time: '16:00', unpaid_break_min: 30, sort_order: shifts.length + 1 }),
    });
    load();
  };
  const delShift = async (id: number) => {
    await authFetch(`${API}/statistics/shift-templates/${id}`, { method: 'DELETE' });
    load();
  };

  const patchProfile = (u: string, patch: Partial<Profile>) =>
    setProfiles(prev => prev.map(p => p.username === u ? { ...p, ...patch } : p));
  const toggleDay = (u: string, day: number) => setProfiles(prev => prev.map(p => {
    if (p.username !== u) return p;
    const has = p.weekly_days.includes(day);
    return { ...p, weekly_days: has ? p.weekly_days.filter(d => d !== day) : [...p.weekly_days, day].sort() };
  }));
  const saveProfile = async (p: Profile) => {
    setSavingP(p.username);
    await authFetch(`${API}/statistics/worker-profiles/${encodeURIComponent(p.username)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        worker_type: p.worker_type, country: p.country, default_shift_id: p.default_shift_id,
        weekly_days: p.weekly_days, active: p.active, tracked: p.tracked,
      }),
    });
    setSavingP(null);
    patchProfile(p.username, { configured: true });
  };

  const inp = "bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1 focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-8">
      <p className="text-gray-500 text-sm">Who is employed, on what terms, and the shift templates that pre-fill the calendar. This drives the expected hours and days across Work time.</p>

      {/* Shift templates */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Shift templates</div>
        <div className="space-y-2">
          {shifts.map(s => (
            <div key={s.id} className="bg-[#1a1d27] rounded-xl border border-gray-800 px-4 py-3 flex flex-wrap items-center gap-3">
              <input className={`${inp} w-44`} value={s.name} onChange={e => patchShift(s.id, { name: e.target.value })} />
              <label className="text-xs text-gray-500">Start <input type="time" className={`${inp} ml-1`} value={s.start_time} onChange={e => patchShift(s.id, { start_time: e.target.value })} /></label>
              <label className="text-xs text-gray-500">End <input type="time" className={`${inp} ml-1`} value={s.end_time} onChange={e => patchShift(s.id, { end_time: e.target.value })} /></label>
              <label className="text-xs text-gray-500">Lunch <input type="number" min={0} max={180} className={`${inp} ml-1 w-16`} value={s.unpaid_break_min} onChange={e => patchShift(s.id, { unpaid_break_min: parseInt(e.target.value) || 0 })} /> min</label>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => saveShift(s)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg">{savingS === s.id ? 'Saving…' : 'Save'}</button>
                <button onClick={() => delShift(s.id)} className="px-3 py-1.5 bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 text-xs rounded-lg">Delete</button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addShift} className="mt-2 flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
          <span className="text-lg leading-none">+</span> New shift
        </button>
      </div>

      {/* People & employment — replaces the old worker_profiles table.
          Terms now live on employment_periods, so this is the single place they are edited. */}
      <EmploymentPanel />

      {/* Day-counting threshold */}
      <DayThresholdPanel />

      <HelpPanel items={[
        { label: 'Shift templates', desc: 'Reusable start/end/lunch presets. They only pre-fill the calendar — editing one never changes days that have already passed.' },
        { label: 'New shift / Save / Delete (shift)', desc: 'Add, edit or remove a shift preset. Save stores that one shift.' },
        { label: 'People — currently here', desc: 'Only the people who are with us now, so the tables never fill up with ex-staff. Employment is a list of periods, not a flag.' },
        { label: 'The three tables', desc: 'Split because they need different things done to them. Nobody is filed by hand — which table someone appears in follows from their terms, so it can never disagree with their contract. Hired workers = fixed weekly days. Temporary workers = anything else (agreed dates, monthly hours, on call). Management = "Track work time" is off.' },
        { label: 'Moving someone between tables', desc: 'Change the thing itself and the table follows. Terms → Contract moves a person between Hired and Temporary; Terms → Track work time moves them in or out of Management. Use "Terms changed" for a real change so both sets of terms stay in the history, and "Fix a mistake" only when the record was simply wrong.' },
        { label: 'Commitment (temporary workers)', desc: 'What that person actually owes, which differs by contract. Monthly hours shows the agreed total — in red if none has been set, because they would expect nothing at all. Agreed dates shows whether a date has to be worked in full or only past the day threshold; the dates themselves are picked on the Calendar tab.' },
        { label: 'Rehire a former worker', desc: 'Former workers are hidden from the table but never lost. This looks them up so you can open a new period on their original account. Their history and their username are kept forever, so a future hire can never inherit someone else\'s picked/packed record. They also still appear in the calendar, exports and P&P statistics for the months they actually worked.' },
        { label: 'Contract', desc: 'Fixed weekly days and Agreed dates are tracked by BOTH days and hours. Monthly hours and On call are tracked by hours only — they show "hours only" and the day threshold never applies to them.' },
        { label: 'Terms → Terms changed', desc: 'For a real change (temp → permanent, different days). Closes the current period and opens a new one from the date you pick, so both sets of terms stay in the history.' },
        { label: 'Terms → Fix a mistake', desc: 'For when the record is simply wrong. Corrects the period in place without adding anything to the timeline.' },
        { label: 'Notice', desc: 'Records that someone has announced they are leaving, with the timestamp. The notice period follows Estonian law from whether they were still in katseaeg on that date: 15 calendar days during it, 30 after. Katseaeg is taken from their contract if set, otherwise assumed to be the statutory maximum of 4 months from their start date (and flagged as an assumption). You can agree an earlier last day; both dates are kept. Their login stays on while they work the notice.' },
        { label: 'End', desc: 'The last step of a leaver. Switches the login off, closes the employment period, and records the reason and leaver checklist. Deletes nothing — the person stops accruing expected hours from their last day, but every hour, pick and export they are already in stays exactly as it is. Notice deliberately does NOT switch the login off, because they are still working their notice, so End is what actually takes their access away.' },
        { label: 'Left — still to close off', desc: 'People whose last day has gone by while their account is still open. They stay listed here, rather than disappearing the morning after, because there is still something to do: until you press End they can go on signing in. The date is already filled in with the last day you agreed, so closing it off late never moves their leaving date.' },
        { label: 'Rehire', desc: 'Starts a new period on the existing account and switches the login back on. Earlier periods stay untouched, so a gap in service stays visible.' },
        { label: 'History', desc: 'Every employment period plus the full audit trail — who changed what and when.' },
        { label: 'Track work time', desc: 'Off = the person keeps their login but never appears in the calendar, dashboard or exports (e.g. owners).' },
        { label: 'Count days', desc: 'Off = hours only. Turn off for casual/extra people, where owing hours is what matters rather than whether a day was covered.' },
        { label: 'Counts as a work day', desc: 'The minimum actually worked for a day to count, measured AFTER any hours used to work back an earlier shortfall — that is what stops overtime from papering over a missed day. Versioned: a change applies from its date forward and never re-counts the past.' },
      ]} />
    </div>
  );
}
